import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import type { 
  SyncSchedule, 
  SyncScheduleCreate, 
  SyncContentType, 
  SyncFrequency,
  Collection 
} from '../types'
import { CONTENT_TYPE_LABELS, CONTENT_TYPE_ICONS, FREQUENCY_LABELS } from '../types'

type Props = {
  schedule?: SyncSchedule | null
  collections: Collection[]
  onSubmit: (data: SyncScheduleCreate) => Promise<boolean>
  onCancel: () => void
  loading?: boolean
}

const CONTENT_TYPES: SyncContentType[] = ['page', 'post', 'sfwd-lessons', 'sfwd-topic', 'product']
const FREQUENCIES: SyncFrequency[] = ['6h', 'daily', 'weekly']
const HOURS = Array.from({ length: 24 }, (_, i) => i)
const DAYS = [
  { value: 0, label: 'Lundi' },
  { value: 1, label: 'Mardi' },
  { value: 2, label: 'Mercredi' },
  { value: 3, label: 'Jeudi' },
  { value: 4, label: 'Vendredi' },
  { value: 5, label: 'Samedi' },
  { value: 6, label: 'Dimanche' },
]

export function SyncScheduleForm({ schedule, collections, onSubmit, onCancel, loading }: Props) {
  const isEdit = !!schedule
  
  const [name, setName] = useState(schedule?.name || '')
  const [contentType, setContentType] = useState<SyncContentType>(schedule?.content_type || 'post')
  const [targetCollectionId, setTargetCollectionId] = useState<number | null>(schedule?.target_collection_id || null)
  const [frequency, setFrequency] = useState<SyncFrequency>(schedule?.frequency || 'daily')
  const [preferredHour, setPreferredHour] = useState(schedule?.preferred_hour ?? 3)
  const [preferredDay, setPreferredDay] = useState(schedule?.preferred_day ?? 0)
  const [enabled, setEnabled] = useState(schedule?.enabled ?? true)
  const [submitting, setSubmitting] = useState(false)

  // Auto-generate name on content type change (create mode only)
  useEffect(() => {
    if (!isEdit && !name) {
      setName(`Sync ${CONTENT_TYPE_LABELS[contentType]}`)
    }
  }, [contentType, isEdit, name])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    
    setSubmitting(true)
    const data: SyncScheduleCreate = {
      name: name.trim(),
      content_type: contentType,
      target_collection_id: targetCollectionId,
      frequency,
      preferred_hour: preferredHour,
      preferred_day: preferredDay,
      enabled,
    }
    
    const success = await onSubmit(data)
    setSubmitting(false)
    
    if (success) {
      onCancel()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-200 dark:border-zinc-700">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {isEdit ? 'Modifier la configuration' : 'Nouvelle synchronisation'}
          </h2>
          <button
            onClick={onCancel}
            className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Nom de la configuration
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex: Sync Articles Blog"
              required
              maxLength={100}
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 
                bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100
                focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Content Type (disabled in edit mode) */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Type de contenu WordPress
            </label>
            <div className="grid grid-cols-2 gap-2">
              {CONTENT_TYPES.map(type => (
                <button
                  key={type}
                  type="button"
                  disabled={isEdit}
                  onClick={() => !isEdit && setContentType(type)}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all
                    ${contentType === type
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'border-zinc-200 dark:border-zinc-600 hover:border-zinc-300 dark:hover:border-zinc-500'}
                    ${isEdit ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                  `}
                >
                  <span className="text-lg">{CONTENT_TYPE_ICONS[type]}</span>
                  <span>{CONTENT_TYPE_LABELS[type]}</span>
                </button>
              ))}
            </div>
            {isEdit && (
              <p className="mt-1 text-xs text-zinc-500">Le type de contenu ne peut pas être modifié</p>
            )}
          </div>
          
          {/* Target Collection */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Collection cible (optionnel)
            </label>
            <select
              value={targetCollectionId ?? ''}
              onChange={e => setTargetCollectionId(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 
                bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100
                focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Aucune collection spécifique</option>
              {collections.map(col => (
                <option key={col.id} value={col.id}>{col.title}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-zinc-500">
              Les contenus importés seront ajoutés à cette collection
            </p>
          </div>
          
          {/* Frequency */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Fréquence de synchronisation
            </label>
            <div className="flex gap-2">
              {FREQUENCIES.map(freq => (
                <button
                  key={freq}
                  type="button"
                  onClick={() => setFrequency(freq)}
                  className={`
                    flex-1 px-3 py-2 rounded-lg border text-sm transition-all
                    ${frequency === freq
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'border-zinc-200 dark:border-zinc-600 hover:border-zinc-300'}
                  `}
                >
                  {FREQUENCY_LABELS[freq]}
                </button>
              ))}
            </div>
          </div>
          
          {/* Preferred Hour */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                Heure préférée (UTC)
              </label>
              <select
                value={preferredHour}
                onChange={e => setPreferredHour(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 
                  bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100
                  focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {HOURS.map(h => (
                  <option key={h} value={h}>{h.toString().padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            
            {/* Preferred Day (only for weekly) */}
            {frequency === 'weekly' && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Jour de la semaine
                </label>
                <select
                  value={preferredDay}
                  onChange={e => setPreferredDay(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 
                    bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100
                    focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {DAYS.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          {/* Enabled toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Activer la synchronisation
              </p>
              <p className="text-xs text-zinc-500">
                La synchronisation démarrera automatiquement selon la planification
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEnabled(!enabled)}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                ${enabled ? 'bg-blue-600' : 'bg-zinc-300 dark:bg-zinc-600'}
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                  ${enabled ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200 dark:border-zinc-700">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium rounded-lg
                text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || loading || !name.trim()}
              className="px-4 py-2 text-sm font-medium rounded-lg
                bg-blue-600 text-white hover:bg-blue-700
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Enregistrement...' : (isEdit ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SyncScheduleForm
